import React, { useEffect, useState, useRef } from 'react';
import { Container } from './styles';

interface InputProps extends React.HTMLAttributes<HTMLInputElement> {
    placeholder?: string;
    value: string | any;
    name?: string;
    type?: 'text' | 'password' | 'email' | 'tel' | 'number';
    mask?: any;
    onChangeText: (value: string | any) => void;
    search?: boolean;
    className?: any;
    title?: string;
    disabled?: boolean;
    error?: string;
    obrigatorio?: boolean;
}

const InputMoney: React.FC<InputProps> = ({
    name,
    value,
    placeholder = 'R$ 0,00',
    onChangeText,
    mask = '',
    type,
    title = null,
    className = '',
    disabled = false,
    error = '',
    obrigatorio = false,
    ...rest
}) => {
    const [displayValue, setDisplayValue] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    const formatCurrency = (value: string): string => {
        const numbers = value.replace(/\D/g, '');
        
        if (!numbers || numbers === '0') {
            return 'R$ 0,00';
        }

        const amount = parseInt(numbers, 10);
        
        const formatted = (amount / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        return formatted;
    };

    const getNumericValue = (formattedValue: string): string => {
        const numbers = formattedValue.replace(/\D/g, '');
        if (!numbers) return '0.00';
        
        const amount = parseInt(numbers, 10);
        return (amount / 100).toFixed(2);
    };

    useEffect(() => {
        if (value !== undefined) {
            const numericValue = typeof value === 'number' ? value.toString() : value;
            if (numericValue && numericValue !== '0' && numericValue !== '0.00') {
                if (numericValue.includes('R$')) {
                    setDisplayValue(numericValue);
                } else {
                    const cleanValue = numericValue.replace(/\D/g, '');
                    if (cleanValue) {
                        if (numericValue.includes('.')) {
                            const reais = parseFloat(numericValue) * 100;
                            setDisplayValue(formatCurrency(Math.round(reais).toString()));
                        } else {
                            setDisplayValue(formatCurrency(cleanValue));
                        }
                    } else {
                        setDisplayValue('R$ 0,00');
                    }
                }
            } else {
                setDisplayValue('R$ 0,00');
            }
        } else {
            setDisplayValue('R$ 0,00');
        }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        
        if (!inputValue || inputValue === 'R$ ' || inputValue === 'R$') {
            setDisplayValue('R$ 0,00');
            onChangeText('0.00');
            return;
        }

        const formatted = formatCurrency(inputValue);
        setDisplayValue(formatted);
        
        const numericValue = getNumericValue(formatted);
        onChangeText(numericValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true) ||
            (e.keyCode >= 35 && e.keyCode <= 39)) {
            return;
        }
        
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault();
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setTimeout(() => {
            const input = e.target;
            input.setSelectionRange(input.value.length, input.value.length);
        }, 0);
    };

    return (
        <Container className={`default-input ${className} ${error ? 'error' : ''}`}>
            {title && <p className="title">
                {title}
                {(obrigatorio || error) && <span>*</span>}
            </p>}
            <input
                ref={inputRef}
                type="text"
                name={name}
                value={displayValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                placeholder={placeholder}
                disabled={disabled}
                {...rest}
            />
            {error && error !== '*' && <span className="error-message">{error}</span>}
        </Container>
    );
};

export default InputMoney;